import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Activity, AlertTriangle, CheckCircle2, Truck, HardHat,
  ClipboardList, FolderTree, ShieldAlert, Database, Briefcase,
  Wrench, Radio, CloudRain,
} from "lucide-react";

/**
 * LiveOperationsPreview — cinematic, slimmed-down operational heartbeat
 * embedded into /demo ABOVE the role-tiles section. NOT a full dashboard.
 *
 * Goal: visitors should FEEL a live contractor operation BEFORE choosing
 * a role. Three controlled bands:
 *   1. KPI strip       — 5 numbers that matter on a real job
 *   2. Live activity feed (last 5 events, color-coded by severity)
 *   3. Auto-routing destinations with active pulse counts
 *
 * Restraint over flash. Mission-critical, not gamer-UI.
 */
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Consequence chain — what the system did AUTOMATICALLY after the event.
// Keyed off `kind` so it stays consistent whether the event came from the
// API or the DEFAULT_EVENTS fallback. Empty string skips the row.
function consequenceFor(e) {
  if (e.consequence) return e.consequence;
  switch (e.kind) {
    case "PRE-OP FAIL":     return "→ EQUIPMENT LOCKED OUT · Shop queue · Safety alerted";
    case "INCIDENT":        return "→ Safety + PM + Admin notified · investigation opened";
    case "DAILY REPORT":    return "→ PDF routed · PM Folder + Job Folder + Backup";
    case "QA/QC":           return "→ PM · Admin · Test-Lab cc'd · record stored";
    case "SAFETY MEETING":  return "→ Crew records updated · Safety Folder · attendance log";
    case "JHP ACK":         return "→ Operator's training record updated · Safety Folder";
    case "PRE-OP":          return "→ Walk-around stored · clearance logged";
    case "WEATHER DELAY":   return "→ Schedule impact tracked · PM notified";
    default:                return "";
  }
}

const SEV_COLOR = {
  critical: "#FF6A6A",
  warning:  "#FFD000",
  success:  "#65DA76",
  info:     "#FF6A00",
};

const SEV_BG = {
  critical: "rgba(211,47,47,0.12)",
  warning:  "rgba(255,208,0,0.10)",
  success:  "rgba(46,125,50,0.10)",
  info:     "rgba(255,106,0,0.10)",
};

const KIND_ICON = {
  "DAILY REPORT":    ClipboardList,
  "PRE-OP":          Truck,
  "PRE-OP FAIL":     Truck,
  "INCIDENT":        AlertTriangle,
  "SAFETY MEETING":  HardHat,
  "QA/QC":           CheckCircle2,
  "JHP ACK":         ShieldAlert,
  "WEATHER DELAY":   CloudRain,
};

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24)  return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

function nowStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export default function LiveOperationsPreview() {
  const [feed, setFeed]   = useState([]);
  const [jobs, setJobs]   = useState([]);
  const [equip, setEquip] = useState([]);
  const [folders, setFolders] = useState([]);
  const [stamp, setStamp] = useState(nowStamp());

  useEffect(() => {
    let cancel = false;
    Promise.all([
      axios.get(`${API}/demo/activity-feed`).catch(() => null),
      axios.get(`${API}/jobs`).catch(() => null),
      axios.get(`${API}/equipment-master`).catch(() => null),
      axios.get(`${API}/demo/folders`).catch(() => null),
    ]).then(([f, j, e, fl]) => {
      if (cancel) return;
      setFeed(f?.data?.events || []);
      setJobs(j?.data?.items   || []);
      setEquip(e?.data?.items  || []);
      setFolders(fl?.data?.folders || []);
    });
    const t = setInterval(() => setStamp(nowStamp()), 1000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  const oosCount = useMemo(
    () => equip.filter((u) => u.out_of_service === "Yes" || u.out_of_service === true).length,
    [equip],
  );

  const incidentsCount = useMemo(
    () => feed.filter((e) => e.kind === "INCIDENT").length,
    [feed],
  );

  // Roughly today's reports — count entries from feed kind === DAILY REPORT
  const reportsToday = useMemo(
    () => feed.filter((e) => e.kind === "DAILY REPORT").length || 30,
    [feed],
  );

  // Synthetic but realistic manpower number — derived from jobs count so
  // it stays consistent with the seed (no random churn that erodes trust).
  const manpower = Math.max(28, jobs.length * 11 + 4);

  return (
    <section
      data-testid="live-ops-preview"
      className="relative max-w-7xl mx-auto px-5 sm:px-8"
    >
      {/* Subtle blueprint grid behind the preview */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 bottom-0 pointer-events-none opacity-30"
        style={{
          background:
            "linear-gradient(rgba(255,106,0,0.04) 1px, transparent 1px), " +
            "linear-gradient(90deg, rgba(255,106,0,0.04) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          maskImage: "radial-gradient(ellipse at center, black 60%, transparent 100%)",
        }}
      />

      <div
        className="relative rounded-xl border-2 overflow-hidden"
        style={{ background: "#0B0B0B", borderColor: "#1F1F1F" }}
      >
        {/* Top header strip */}
        <div
          className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap border-b"
          style={{ background: "#070707", borderColor: "#1A1A1A" }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-md shrink-0"
              style={{ background: "rgba(255,106,0,0.15)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.45)" }}
            >
              <Radio className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FF6A00" }}>
                LIVE OPERATIONS · RIGHT NOW
              </div>
              <div className="font-display text-base sm:text-lg font-black text-white leading-tight truncate">
                A working contractor — captured in motion.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <span className="inline-flex items-center gap-1.5 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.25em]" style={{ color: "#65DA76" }}>
              <span className="inline-flex w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#2E7D32" }} />
              SYSTEM HEALTHY
            </span>
            <span className="font-mono text-[10px] hidden sm:inline-block" style={{ color: "#8A8A8A" }}>
              {stamp}
            </span>
          </div>
        </div>

        {/* KPI strip — 5 compact tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px" style={{ background: "#1A1A1A" }}>
          <Kpi label="Active Jobs"        value={jobs.length || 4}  icon={FolderTree}     accent="#FF6A00" />
          <Kpi label="Manpower On Shift"  value={manpower}          icon={HardHat}        accent="#FFD000" sub="union + sub" />
          <Kpi label="Equipment OOS"      value={oosCount || 5}     denom={equip.length || 8}  icon={Truck}     accent={(oosCount || 5) > 0 ? "#FF6A6A" : "#65DA76"} />
          <Kpi label="Open Incidents"     value={incidentsCount || 2} icon={AlertTriangle} accent="#FFB000" sub="under review" />
          <Kpi label="Reports · Today"    value={reportsToday}      icon={ClipboardList}  accent="#65DA76" sub="all routed" />
        </div>

        {/* Body — live feed + auto-routing chips */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px" style={{ background: "#1A1A1A" }}>
          {/* Live activity feed */}
          <div
            className="lg:col-span-7 px-4 sm:px-6 py-4"
            style={{ background: "#0B0B0B" }}
            data-testid="live-ops-preview-feed"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-sm font-black tracking-tight text-white inline-flex items-center gap-2">
                <Activity className="w-3.5 h-3.5" style={{ color: "#FF6A00" }} />
                Real-time activity
              </h3>
              <span className="font-mono text-[9px] uppercase tracking-[0.25em]" style={{ color: "#8A8A8A" }}>
                last {Math.min(feed.length || 5, 5)} events
              </span>
            </div>
            <div className="space-y-1.5">
              {(feed.length ? feed : DEFAULT_EVENTS).slice(0, 5).map((e, i) => {
                const Icon = KIND_ICON[e.kind] || Activity;
                const c = SEV_COLOR[e.severity] || SEV_COLOR.info;
                const bg = SEV_BG[e.severity] || SEV_BG.info;
                return (
                  <div
                    key={`${e.id || i}-${e.kind}`}
                    className="flex items-center gap-3 px-3 py-2 rounded-md border transition-colors"
                    style={{
                      background: bg,
                      borderColor: i === 0 ? "rgba(255,106,0,0.5)" : "#1A1A1A",
                    }}
                    data-testid="live-ops-event"
                  >
                    <span
                      className="inline-flex items-center justify-center w-7 h-7 rounded shrink-0"
                      style={{ background: "#0A0A0A", color: c, border: `1px solid ${c}55` }}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[9px] uppercase tracking-[0.25em] shrink-0" style={{ color: c }}>
                          {e.kind}
                        </span>
                        {e.job_number && (
                          <span
                            className="font-mono text-[9px] px-1.5 rounded shrink-0"
                            style={{ background: "rgba(255,106,0,0.12)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}
                          >
                            {e.job_number}
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] sm:text-sm text-white font-semibold truncate leading-tight mt-0.5">
                        {e.title}
                      </div>
                      {e.subtitle && (
                        <div className="text-[10px] sm:text-[11px] truncate" style={{ color: "#BDBDBD" }}>
                          {e.subtitle}
                        </div>
                      )}
                      {(() => {
                        const cons = consequenceFor(e);
                        if (!cons) return null;
                        return (
                          <div
                            className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.15em] truncate mt-0.5"
                            style={{ color: c }}
                          >
                            {cons}
                          </div>
                        );
                      })()}
                    </div>
                    <span className="font-mono text-[10px] shrink-0" style={{ color: "#8A8A8A" }}>
                      {timeAgo(e.at)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Auto-routing column */}
          <div
            className="lg:col-span-5 px-4 sm:px-6 py-4"
            style={{ background: "#0B0B0B" }}
            data-testid="live-ops-preview-routing"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-sm font-black tracking-tight text-white inline-flex items-center gap-2">
                Auto-routing
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] px-1.5 rounded" style={{ background: "rgba(255,106,0,0.15)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.4)" }}>
                  ACTIVE
                </span>
              </h3>
              <span className="font-mono text-[9px] uppercase tracking-[0.25em]" style={{ color: "#8A8A8A" }}>
                every submit
              </span>
            </div>

            <p className="text-[11px] leading-relaxed mb-3" style={{ color: "#BDBDBD" }}>
              Field forms land in PM folders, Safety, Job records, and the
              backup mirror — automatically. No email. No paperwork delay.
            </p>

            <div className="space-y-1.5">
              {(folders.length ? folders : DEFAULT_FOLDERS).slice(0, 5).map((f, i) => (
                <RoutingRow key={f.label || f.name || i} folder={f} />
              ))}
            </div>

            <div className="mt-3 pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: "#1A1A1A" }}>
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] inline-flex items-center gap-1.5" style={{ color: "#65DA76" }}>
                <span className="inline-flex w-1.5 h-1.5 rounded-full" style={{ background: "#2E7D32" }} />
                Backups · 14d retention · off-site mirror
              </span>
            </div>
          </div>
        </div>

        {/* Bottom strip — domain capabilities + CTA hint */}
        <div
          className="px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 flex-wrap border-t"
          style={{ background: "#070707", borderColor: "#1A1A1A" }}
        >
          <div className="flex items-center gap-2 sm:gap-3 font-mono text-[9px] uppercase tracking-[0.3em] flex-wrap" style={{ color: "#8A8A8A" }}>
            <span style={{ color: "#FF6A00" }}>RUNS</span>
            <span>·</span>
            <span>TRACKS</span>
            <span>·</span>
            <span>DOCUMENTS</span>
            <span>·</span>
            <span>ROUTES</span>
            <span>·</span>
            <span>ESCALATES</span>
            <span>·</span>
            <span>BACKS UP</span>
          </div>
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] inline-flex items-center gap-1.5" style={{ color: "#FF6A00" }}>
            <span className="inline-flex w-1 h-1 rounded-full animate-pulse" style={{ background: "#FF6A00" }} />
            Pick a role below to step inside
          </span>
        </div>
      </div>
    </section>
  );
}

function Kpi({ label, value, denom, sub, icon: Icon, accent }) {
  return (
    <div
      className="px-3 sm:px-4 py-3 flex items-center gap-3"
      style={{ background: "#0B0B0B" }}
      data-testid="live-ops-kpi"
    >
      <span
        className="inline-flex items-center justify-center w-9 h-9 rounded-md shrink-0"
        style={{ background: `${accent}1F`, color: accent, border: `1px solid ${accent}55` }}
      >
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] truncate" style={{ color: "#BDBDBD" }}>
          {label}
        </div>
        <div className="flex items-baseline gap-1.5">
          <div className="font-display text-xl sm:text-2xl font-black leading-none" style={{ color: accent }}>
            {value}
          </div>
          {denom != null && (
            <div className="font-mono text-[10px]" style={{ color: "#8A8A8A" }}>
              / {denom}
            </div>
          )}
          {sub && (
            <div className="font-mono text-[10px] truncate" style={{ color: "#8A8A8A" }}>
              {sub}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RoutingRow({ folder }) {
  const label = folder.label || folder.name || "Destination";
  const count = folder.count ?? folder.total ?? folder.items;
  const Icon = label.toLowerCase().includes("safety") ? ShieldAlert
            : label.toLowerCase().includes("pm") ? Briefcase
            : label.toLowerCase().includes("shop") ? Wrench
            : label.toLowerCase().includes("backup") ? Database
            : FolderTree;
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-md border"
      style={{ background: "#0F0F0F", borderColor: "#1F1F1F" }}
      data-testid="live-ops-routing-row"
    >
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded shrink-0"
        style={{ background: "rgba(255,106,0,0.10)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}
      >
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white truncate">
          {label}
        </div>
      </div>
      <span
        className="font-mono text-[10px] px-2 py-0.5 rounded shrink-0"
        style={{ background: "rgba(255,106,0,0.08)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}
      >
        {typeof count === "number" ? `${count} routed` : "auto"}
      </span>
    </div>
  );
}

// Fallbacks — used only if the API is unreachable. Numbers and copy stay
// consistent with the seeded rich data so the demo never feels empty.
const DEFAULT_EVENTS = [
  { kind: "DAILY REPORT",   title: "FO-1003 · I-95 MOT — submitted",           subtitle: "Auto-routed · PM Folder · Job Folder · Backup", severity: "info",     at: new Date(Date.now() - 2*60*1000).toISOString(),  job_number: "FO-1003" },
  { kind: "PRE-OP FAIL",    title: "EX-210 · brake-check FAIL — locked out",   subtitle: "Shop Queue notified · Safety alerted",          severity: "critical", at: new Date(Date.now() - 11*60*1000).toISOString(), job_number: "FO-1001" },
  { kind: "INCIDENT",       title: "Near-miss · Pinch point · Sta 245+00",      subtitle: "Safety + PM + Admin notified · Investigation opened", severity: "warning", at: new Date(Date.now() - 38*60*1000).toISOString(), job_number: "FO-1002" },
  { kind: "QA/QC",          title: "Concrete pour · 42 CY · 4000 PSI — PASS",  subtitle: "PM · Admin · Test-Lab cc",                       severity: "success",  at: new Date(Date.now() - 75*60*1000).toISOString(), job_number: "FO-1004" },
  { kind: "SAFETY MEETING", title: "Toolbox · Trench safety · 9 attendees",     subtitle: "Crew records updated · Safety Folder",          severity: "info",     at: new Date(Date.now() - 120*60*1000).toISOString(), job_number: "FO-1003" },
];

const DEFAULT_FOLDERS = [
  { label: "PM Folder",      count: 47 },
  { label: "Safety Folder",  count: 33 },
  { label: "Job Folder",     count: 92 },
  { label: "Shop Queue",     count: 11 },
  { label: "Backup Mirror",  count: 14 },
];
